import { Form, Formik } from 'formik';
import TextInput from '@atoms/TextInput';
import PasswordInput from '@atoms/PasswordInput';
import { ButtonPrimaryLarge } from '@styled';
import Checkbox from '@atoms/Checkbox';
import React, { useContext, useState } from 'react';
import { jsonLogin } from '@general/api/memberAPI';
import { LoadingContext } from '@context/LoadingProvider';
import MembershipCta from './MembershipCta';
import { PropTypes } from 'prop-types';
import { Link, useLocation } from 'react-router-dom';
import AuthenticationErrorNotice from './AuthenticationErrorNotice';
import useStytchQueryString from '../../../hooks/useStytchQueryString';
import CheckboxWithoutForm from '@atoms/CheckboxWithoutForm';

const PasswordResetRequiredNotice = () => {
    const { search } = useLocation();

    return (
        <>
            <h2 className="display5 text-center bold mb-3">Password Reset Required</h2>

            <p className="text-center mb-3 red-hat" style={{ fontSize: '1rem', lineHeight: 1.4, width: '95%' }}>
                In order to keep your account secure, we occasionally require you to reset your password. If there is an
                account associated with the credentials you provided, an email has been sent to the address on file with
                a link to reset your password.
            </p>

            <p className="text-center mb-3 red-hat" style={{ fontSize: '1rem', lineHeight: 1.4, width: '95%', fontWeight: 700 }}>
                If you do not see the email, please check your spam folder and whitelist login@icsc.com.
            </p>

            <hr className="w-100 my-4" />

            <p className="text-center mb-4 red-hat" style={{ fontSize: '1rem', lineHeight: 1.4, width: '95%' }}>
                <a className="accent" href={`/login${search}`}>
                    Go Back to the Log In Page.
                </a>
            </p>
        </>
    );
};

const LockedAccountNotice = () => {
    const { search } = useLocation();

    return (
        <>
            <h2 className="display5 text-center bold mb-3">Account Locked</h2>

            <p className="text-center mb-3 red-hat" style={{ fontSize: '1rem', lineHeight: 1.4, width: '95%' }}>
                Please try again later. If you continue to have trouble logging in, contact
                <a className="accent" href="mailto:membership@icsc.com">
                    membership@icsc.com
                </a>
                .
            </p>

            <hr className="w-100 my-4" />

            <p className="text-center mb-4 red-hat" style={{ fontSize: '1rem', lineHeight: 1.4, width: '95%' }}>
                <a className="accent" href={`/login${search}`}>
                    Go Back to the Log In Page.
                </a>
            </p>
        </>
    );
};

const LoginWithPassword = ({ email }) => {
    const { setIsLoading } = useContext(LoadingContext);
    const { search } = useLocation();
    const stytchQueryString = useStytchQueryString();

    const [username, setUsername] = useState(email ?? '');
    const [password, setPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(false);
    const [locked, setLocked] = useState(false);
    const [passwordResetRequired, setPasswordResetRequired] = useState(false);
    const [error, setError] = useState(null);

    const login = async () => {
        setError(null);
        setIsLoading(true);

        try {
            await jsonLogin(
                username,
                password,
                `${window.location.origin}/login/reset-password/authenticate${stytchQueryString}`,
                `${window.location.origin}/login/magic-link/authenticate${stytchQueryString}`,
                rememberMe,
            );
            window.location = new URLSearchParams(search.slice(1)).get('returnUrl') ?? '/';
        } catch (e) {
            const attemptsRemaining = e.response.data.attempts_remaining;

            setIsLoading(false);

            if (e.response.data?.error === 'Account locked.') {
                setLocked(true);

                return;
            }

            if (e.response.data?.error === 'reset_password') {
                setPasswordResetRequired(true);

                return;
            }

            setError(
                `Your username or password is incorrect. You can try again ${attemptsRemaining} time${
                    attemptsRemaining !== 1 ? 's' : ''
                } before your account is locked to prevent unauthorized use.`
            );
        }
    };

    if (passwordResetRequired) {
        return <PasswordResetRequiredNotice />;
    }

    if (locked) {
        return <LockedAccountNotice />;
    }

    return (
        <>
            <h2 className="display5 text-center bold mb-4">Log in to your account</h2>

            {error && <AuthenticationErrorNotice error={error} />}

            <Formik initialValues={{ username, password, rememberMe }} onSubmit={login}>
                {() => (
                    <Form className="mb-4">
                        <TextInput
                            label="Email Address or Member ID#"
                            placeholder="Email Address or Member ID#"
                            name="username"
                            value={username}
                            onChange={e => {
                                setUsername(e.target.value);
                            }}
                            required
                            autoFocus={!email}
                        />
                        <PasswordInput
                            label="Password"
                            placeholder="Password"
                            name="password"
                            value={password}
                            error={null}
                            onChange={e => {
                                setPassword(e.target.value);
                            }}
                            required
                            autoFocus={!!email}
                        />
                        <div className="mb-2">
                            <CheckboxWithoutForm checked={rememberMe} onCheckChange={checked => setRememberMe(checked)}>
                                Remember Me
                            </CheckboxWithoutForm>
                        </div>
                        <ButtonPrimaryLarge className="w-100 mt-1">Sign In</ButtonPrimaryLarge>
                    </Form>
                )}
            </Formik>

            <div className="text-center">
                <p className="display8 mb-2">Can’t remember your password?</p>
                <Link to={`/login/magic-link${search}`} className="d-block display8 accent mb-2">
                    Log In Without a Password
                </Link>
                <Link to={`/login/reset-password${search}`} className="d-block display8 accent">
                    Reset Your Password
                </Link>
            </div>

            <MembershipCta />
        </>
    );
};

LoginWithPassword.propTypes = {
    email: PropTypes.string,
};

export default LoginWithPassword;
